import { useState } from 'react';

import { Field, Notice } from '@/components/Modal';
import { useSession } from '@/lib/auth/session';
import { isSuperAdmin } from '@/lib/types';

import { useRemoveNasAdminAccount, useSaveNasAdminAccount, useStorageAnalysis } from './useDocuments';

/**
 * NAS 접속 설정 창(톱니바퀴) 안의 <b>관리자 계정</b>(DOC-142 · 사용자 지시 2026-11-02 「톱니바퀴 창 안」).
 *
 * <p>SUPER_ADMIN 이 저장된 Synology 연결을 열었을 때만 선다. 창의 [저장] 과 따로 저장된다 — 저장할 때 서버가
 * 그 계정으로 실제 로그인해 보고, 구글 OTP 6자리를 받는다. 비밀번호는 다시 보이지 않는다.
 */
export function NasAdminAccountSection({ connectionId }: { connectionId: string }) {
  const { data: session } = useSession();
  const allowed = isSuperAdmin(session?.user?.userType);
  const { data: analysis } = useStorageAnalysis(allowed);
  const current = analysis?.sources.find((s) => s.sourceId === connectionId)?.adminUsername ?? null;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const save = useSaveNasAdminAccount();
  const remove = useRemoveNasAdminAccount();
  const pending = save.isPending || remove.isPending;
  const error = save.error?.message ?? remove.error?.message ?? null;
  const done = save.isSuccess || remove.isSuccess;

  if (!allowed) return null;

  const reset = () => {
    setPassword('');
    setCode('');
  };

  return (
    <div className="space-y-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
      <div className="flex items-center justify-between gap-2">
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
          관리자 계정
          <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 500, color: current ? '#15803d' : 'var(--text-muted)' }}>
            {current ? `설정됨 · ${current}` : '없음'}
          </span>
        </span>
        <span className="flex gap-2">
          {current && (
            <button
              type="button"
              className="btn-secondary"
              style={{ fontSize: 12, padding: '4px 10px' }}
              disabled={code.length !== 6 || pending}
              onClick={() => {
                if (!window.confirm('관리자 계정을 지울까요?')) return;
                remove.mutate({ id: connectionId, code }, { onSuccess: reset });
              }}
            >
              계정 지우기
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            style={{ fontSize: 12, padding: '4px 12px' }}
            disabled={!username.trim() || !password || code.length !== 6 || pending}
            onClick={() => save.mutate({ id: connectionId, username: username.trim(), password, code }, { onSuccess: reset })}
          >
            {save.isPending ? '확인 중…' : '관리자 계정 저장'}
          </button>
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="계정">
          <input className="input-field" value={username} placeholder={current ?? ''} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="비밀번호">
          <input className="input-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="구글 OTP">
          <input
            className="input-field"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ letterSpacing: '0.3em', textAlign: 'center' }}
          />
        </Field>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {done && !error && <Notice tone="success">{remove.isSuccess ? '관리자 계정을 지웠습니다.' : '관리자 계정을 저장했습니다.'}</Notice>}
    </div>
  );
}
